/**
 * "Уникальность" — a manual checklist of small, low-visibility video transforms applied to every
 * exported frame, so repeated exports of the same project don't hash/fingerprint identically on
 * social platforms. Mirrors server/src/exporting/uniqueness.ts (cmss's ffmpeg-based counterpart)
 * as closely as the browser's Canvas 2D API allows — see that file's own top comment for the two
 * techniques deliberately NOT offered (flip, split-screen compositing) and why.
 *
 * Applied as a whole-frame post-process in SceneExporter, right after CanvasRenderer.render()
 * paints the composited frame and before it's handed to the encoder — NOT as a per-element render
 * node. That mirrors what happens in cmss (the ffmpeg filters act on the already-composited
 * output), keeps every existing render-node/scene-builder code untouched, and means the transform
 * is applied exactly once per frame regardless of how many elements are on screen.
 *
 * `speedChange` (video+audio tempo, in cmss's table) has no counterpart here — implementing it
 * would mean resampling the whole export's frame-time mapping and the final audio buffer's
 * duration together, which reaches well beyond a per-frame/per-buffer post-process into the
 * exporter's own timing model. Left out rather than shipped half-correct (see the audio module's
 * own top comment for the same reasoning re: pitchShift, which — unlike speed — doesn't need that
 * and IS supported).
 */

export interface UniquenessOptions {
	microCropZoom?: boolean;
	microRotate?: boolean;
	colorCorrection?: boolean;
	filmGrain?: boolean;
	/** Canvas 2D's native blur() filter only — no separate "or sharpen" variant (unlike cmss's
	 * ffmpeg-based unsharp option) since a correct per-frame convolution sharpen in JS is far too
	 * slow for a many-thousand-frame export. */
	lightBlur?: boolean;
	scanlineOverlay?: boolean;
}

const OPTION_KEYS: Array<keyof UniquenessOptions> = [
	"microCropZoom",
	"microRotate",
	"colorCorrection",
	"filmGrain",
	"lightBlur",
	"scanlineOverlay",
];

/** Checks only the 6 video-side keys — a caller-supplied options object may also carry the 5
 * audio-side keys (see export/uniqueness.ts's combined UniquenessOptions), which are irrelevant
 * here and never trigger any of this module's per-frame work. */
export function isVideoUniquenessEnabled(options?: UniquenessOptions | null): boolean {
	if (!options) return false;
	return OPTION_KEYS.some((key) => options[key] === true);
}

/** Resolved once per export (see RendererManager.exportProject) — every frame shares the same
 * random crop/rotation/color/etc. values, so the transform doesn't wobble frame to frame, which
 * would look far more noticeable than any fixed micro-transform alone. */
export interface UniquenessProfile {
	cropFraction: number;
	rotateDeg: number;
	brightness: number;
	contrast: number;
	saturate: number;
	hueRotateDeg: number;
	blurPx: number;
	grainOpacity: number;
	scanlineOpacity: number;
	scanlineSpacing: number;
}

function randRange({ rng, min, max }: { rng: () => number; min: number; max: number }): number {
	return min + rng() * (max - min);
}

function randSign(rng: () => number): 1 | -1 {
	return rng() < 0.5 ? -1 : 1;
}

/** `rng` is injectable purely for deterministic testing — real call sites omit it. */
export function buildUniquenessProfile(rng: () => number = Math.random): UniquenessProfile {
	return {
		cropFraction: randRange({ rng, min: 0.004, max: 0.015 }),
		rotateDeg: randSign(rng) * randRange({ rng, min: 0.4, max: 1.2 }),
		brightness: 1 + randSign(rng) * randRange({ rng, min: 0.01, max: 0.05 }),
		contrast: 1 + randSign(rng) * randRange({ rng, min: 0.01, max: 0.03 }),
		saturate: 1 + randSign(rng) * randRange({ rng, min: 0.02, max: 0.05 }),
		hueRotateDeg: randSign(rng) * randRange({ rng, min: 1, max: 5 }),
		blurPx: randRange({ rng, min: 0.25, max: 0.6 }),
		grainOpacity: randRange({ rng, min: 0.02, max: 0.05 }),
		scanlineOpacity: randRange({ rng, min: 0.015, max: 0.035 }),
		scanlineSpacing: Math.round(randRange({ rng, min: 3, max: 6 })),
	};
}

/** Fixed headroom a rotated, overscanned frame needs so its corners never show a gap at the
 * canvas edges — comfortably covers the profile's whole rotateDeg range (up to 1.2°) for any
 * aspect ratio; see applyUniquenessToFrame's rotate branch. */
const ROTATE_OVERSCAN = 1.06;

/** Per-export scratch resources — a reusable canvas (avoids allocating one every frame) and, for
 * the two tiled effects, a small pre-rendered tile drawn once and repeated every frame instead of
 * regenerating per-pixel noise/lines on each of a many-thousand-frame export. */
export interface UniquenessResources {
	scratch: HTMLCanvasElement;
	scratchCtx: CanvasRenderingContext2D;
	noiseTile?: HTMLCanvasElement;
	scanlineTile?: HTMLCanvasElement;
}

const NOISE_TILE_SIZE = 160;

function buildNoiseTile(): HTMLCanvasElement {
	const tile = document.createElement("canvas");
	tile.width = NOISE_TILE_SIZE;
	tile.height = NOISE_TILE_SIZE;
	const ctx = tile.getContext("2d")!;
	const imageData = ctx.createImageData(NOISE_TILE_SIZE, NOISE_TILE_SIZE);
	for (let i = 0; i < imageData.data.length; i += 4) {
		const v = Math.floor(Math.random() * 256);
		imageData.data[i] = v;
		imageData.data[i + 1] = v;
		imageData.data[i + 2] = v;
		imageData.data[i + 3] = 255;
	}
	ctx.putImageData(imageData, 0, 0);
	return tile;
}

function buildScanlineTile(profile: UniquenessProfile): HTMLCanvasElement {
	const tile = document.createElement("canvas");
	tile.width = 4;
	tile.height = profile.scanlineSpacing;
	const ctx = tile.getContext("2d")!;
	ctx.clearRect(0, 0, tile.width, tile.height);
	ctx.fillStyle = `rgba(255,255,255,${profile.scanlineOpacity})`;
	ctx.fillRect(0, 0, tile.width, 1);
	return tile;
}

export function createUniquenessResources({
	width,
	height,
	options,
	profile,
}: {
	width: number;
	height: number;
	options: UniquenessOptions;
	profile: UniquenessProfile;
}): UniquenessResources | null {
	if (!isVideoUniquenessEnabled(options)) return null;

	const scratch = document.createElement("canvas");
	scratch.width = width;
	scratch.height = height;
	const scratchCtx = scratch.getContext("2d");
	if (!scratchCtx) return null;

	return {
		scratch,
		scratchCtx,
		noiseTile: options.filmGrain ? buildNoiseTile() : undefined,
		scanlineTile: options.scanlineOverlay ? buildScanlineTile(profile) : undefined,
	};
}

function drawTiled({
	ctx,
	tile,
	width,
	height,
}: {
	ctx: CanvasRenderingContext2D;
	tile: HTMLCanvasElement;
	width: number;
	height: number;
}): void {
	const pattern = ctx.createPattern(tile, "repeat");
	if (!pattern) return;
	ctx.save();
	ctx.fillStyle = pattern;
	ctx.fillRect(0, 0, width, height);
	ctx.restore();
}

/**
 * Mutates `canvas` in place — snapshots the current frame into the scratch canvas whenever a
 * geometry or filter pass needs to read while writing (Canvas 2D self-draws are unreliable across
 * browsers once transforms/filters are involved), then composites the enabled effects back onto
 * it in a fixed order: geometry (crop/rotate) first, then color/blur (CSS filter), then the two
 * tiled overlays last (grain, scanlines) so they read as a texture over the final image rather
 * than getting blurred or recolored themselves.
 */
export function applyUniquenessToFrame({
	canvas,
	width,
	height,
	options,
	profile,
	resources,
}: {
	canvas: HTMLCanvasElement;
	width: number;
	height: number;
	options: UniquenessOptions;
	profile: UniquenessProfile;
	resources: UniquenessResources;
}): void {
	const ctx = canvas.getContext("2d");
	if (!ctx) return;
	const { scratch, scratchCtx } = resources;

	if (options.microCropZoom || options.microRotate) {
		scratchCtx.clearRect(0, 0, width, height);
		scratchCtx.drawImage(canvas, 0, 0);
		ctx.clearRect(0, 0, width, height);
		ctx.save();
		if (options.microRotate) {
			ctx.translate(width / 2, height / 2);
			ctx.rotate((profile.rotateDeg * Math.PI) / 180);
			ctx.scale(ROTATE_OVERSCAN, ROTATE_OVERSCAN);
			ctx.translate(-width / 2, -height / 2);
		}
		if (options.microCropZoom) {
			const cw = width * (1 - 2 * profile.cropFraction);
			const ch = height * (1 - 2 * profile.cropFraction);
			const cx = width * profile.cropFraction;
			const cy = height * profile.cropFraction;
			ctx.drawImage(scratch, cx, cy, cw, ch, 0, 0, width, height);
		} else {
			ctx.drawImage(scratch, 0, 0, width, height);
		}
		ctx.restore();
	}

	if (options.colorCorrection || options.lightBlur) {
		const filterParts: string[] = [];
		if (options.colorCorrection) {
			filterParts.push(
				`brightness(${profile.brightness.toFixed(4)})`,
				`contrast(${profile.contrast.toFixed(4)})`,
				`saturate(${profile.saturate.toFixed(4)})`,
				`hue-rotate(${profile.hueRotateDeg.toFixed(2)}deg)`,
			);
		}
		if (options.lightBlur) {
			filterParts.push(`blur(${profile.blurPx.toFixed(2)}px)`);
		}
		scratchCtx.clearRect(0, 0, width, height);
		scratchCtx.drawImage(canvas, 0, 0);
		ctx.save();
		ctx.filter = filterParts.join(" ");
		ctx.clearRect(0, 0, width, height);
		ctx.drawImage(scratch, 0, 0);
		ctx.filter = "none";
		ctx.restore();
	}

	if (options.filmGrain && resources.noiseTile) {
		ctx.save();
		ctx.globalAlpha = profile.grainOpacity;
		ctx.globalCompositeOperation = "overlay";
		drawTiled({ ctx, tile: resources.noiseTile, width, height });
		ctx.restore();
	}

	if (options.scanlineOverlay && resources.scanlineTile) {
		drawTiled({ ctx, tile: resources.scanlineTile, width, height });
	}
}
